import {
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { AuthenticationResultEnum } from "../../common/enums/AuthenticationResultEnum";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { routesByViewID } from "../../routes/routes";
import { RestError } from "../../services/rest-service/RestService";
import storageService from "../../services/storage/storage-service/StorageService";
import { RedirectFromLogoutStorageKey } from "../../views/login-view/LoginViewUtils";
import { UserStateActionTypeEnum, useUserContext } from "../user-context/UserContext";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import { StorageKeys } from "../../services/storage/StorageKeys";
import { useLocation } from "react-router";
import { AppNotificationContext } from "./AppNotificationContext";
import { AppNotificationState, AppNotificationTypeEnum } from "./AppNotificationContextTypes";
import axios from "axios";

const defaultState: AppNotificationState = {
    notificationType: AppNotificationTypeEnum.Info,
    message: undefined,
    title: undefined,
    show: false,
    hideContactInfo: undefined,
    error: undefined
};

export type AppNotificatonContextProviderProps = {
    children: ReactNode;
};

export function AppNotificationContextProvider({ children }: AppNotificatonContextProviderProps) {

    const [, dispatchUserState] = useUserContext();
    const navigateTo = useNavigateTo();
    const { t } = useTranslation();
    const location = useLocation();

    const [appNotificationState, _setAppNotificationState] = useState(defaultState);
    const [closing, setClosing] = useState(false);
    const [redirectPath, setRedirectPath] = useState<string | undefined>();

    const parseErrorCode = useCallback((errorCode: string | undefined): AuthenticationResultEnum | undefined =>
        errorCode && (errorCode in AuthenticationResultEnum)
            ? AuthenticationResultEnum[errorCode as keyof typeof AuthenticationResultEnum]
            : undefined,
        []);

    const processError = useCallback((state: AppNotificationState): AppNotificationState => {

        const newState = {
            ...state
        };

        let newRedirectPath: string | undefined = undefined;

        let resetState = true;
        let logoutRedirect = false;

        const errorCode = parseErrorCode(state.error?.response?.data.errorCode || state.error?.response?.data.error);

        switch (errorCode) {

            case AuthenticationResultEnum.PasswordChangeRequired:
                newState.message = t('errors.authentication.passwordChangeRequired');
                newRedirectPath = `/${routesByViewID.get(UIViewEnum.ChangePassword)?.path}`;
                break;

            case AuthenticationResultEnum.PasswordExpired:
                newState.message = t('errors.authentication.passwordExpired');
                newRedirectPath = `/${routesByViewID.get(UIViewEnum.ChangePassword)?.path}`;
                break;

            case AuthenticationResultEnum.InvalidUser:
            case AuthenticationResultEnum.NoPermissionsForCustomer:
            case AuthenticationResultEnum.DisabledForCustomer:
            case AuthenticationResultEnum.AccountLocked:
            case AuthenticationResultEnum.LoginFailed:
            case AuthenticationResultEnum.InvalidPassword:
            case AuthenticationResultEnum.UnexpectedError:
                newState.message = t('errors.authentication.loginFailed');
                break;

            case AuthenticationResultEnum.SSONotEnabled:
                newState.message = t('errors.authentication.ssoNotEnabled');
                break;

            case AuthenticationResultEnum.SSOUserNotFound:
                newState.message = t('errors.authentication.ssoUserNotFound');
                break;

            case AuthenticationResultEnum.TokenExpired:
                newState.message = t('errors.authentication.tokenExpired');
                newRedirectPath = `/${routesByViewID.get(UIViewEnum.Login)?.path}`;
                logoutRedirect = true;
                break;

            default:
                if (state.error?.response?.status === 401) {
                    newState.message = t('errors.authentication.tokenExpired');
                    newRedirectPath = `/${routesByViewID.get(UIViewEnum.Login)?.path}`;
                    logoutRedirect = true;
                } else {
                    resetState = false;
                    newState.message = state.message ?? t('errors.genericMessage');
                }

                break;
        }

        if (resetState) {
            // DO NOT clear the session
            //storageService.session.clear();

            if (logoutRedirect) {

                if (storageService.local.get(StorageKeys.IsSSO, false)) {
                    newState.message = undefined;
                    newState.show = false;
                }

                storageService.session.set(RedirectFromLogoutStorageKey, true);
                storageService.session.set(StorageKeys.RedirectRestorePath, `${location.pathname}${location.search}`);
            }

            // The user context cannot be updated from within the reducer for the AppNotificationContext =>
            //  AppNotificationContext.tsx:140 Warning: Cannot update a component (`UserContextProvider`)
            //  while rendering a different component (`AppNotificationContextProvider`). To locate the bad setState() call inside `AppNotificationContextProvider`,
            //  follow the stack trace as described in https://reactjs.org/link/setstate-in-render
            //  at AppNotificationContextProvider                
            // So, dispatch after the execution here has completed.
            setTimeout(() => {
                dispatchUserState({
                    type: UserStateActionTypeEnum.ResetUserState
                });
            }, 1000);

        }

        if (newRedirectPath) {
            setRedirectPath(newRedirectPath);
        }

        return newState;
    }, [
        dispatchUserState,
        location.pathname,
        location.search,
        parseErrorCode,
        t
    ]);

    // @@ this provides an opportunity to add some business logic without the need to use a reducer
    // but I wonder if it would be better for consistency to to just use state reduce pattern?
    const setAppNotificationState = useCallback((state: AppNotificationState) => {

        // if there was an offline error do not do anything, offline will be handled elsewhere
        if (state.error) {
            const restError = state.error as RestError;
            if (restError.offline || restError.message === 'cancel' || restError.name === 'CanceledError') {
                //console.log("AppNotificationContext detected offline.")
                return;
            }
        }

        // merge new state with current state
        _setAppNotificationState(prevState => {
            if (state.show && !prevState.show) {
                setRedirectPath(undefined);
                setClosing(false);
            }

            else if (!state.show && prevState.show) {
                setClosing(true);
            }

            const newState = state.show &&
                state.notificationType === AppNotificationTypeEnum.Error &&
                state.error ?
                processError(state) :
                state;

            return {
                ...prevState,
                ...newState
            };
        });
    }, [processError]);

    useEffect(() => {

        // navigate to redirect path
        if (closing && redirectPath) {
            navigateTo(redirectPath);
        }

    }, [closing, redirectPath, navigateTo]);

    return (
        <AppNotificationContext.Provider value={[appNotificationState, setAppNotificationState]}>
            {children}
        </AppNotificationContext.Provider>
    );
}


